import { chapters } from "../helpres";
import { parseVideoSource } from "@/utils/videoSource";
import { createPlaybackLink, createDirectPlaybackLink } from "./playbackLink";
// The INTERCEPTED axios instance (bearer / Session-Id / locale / single-flight
// refresh). NOT `this.$http`: that is vue-axios's raw axios — the utils/https
// install-plugin is never registered in main.js, so prototype `$http` carries
// no auth headers and the BFF answers 401 (bitten in prod, 2026-07-30).
import { $http } from "@/utils/https";

// R2 / video-platform provider. Resurrected from the dead `video-providers/default/`
// (Plyr + native <video> + the shared chapters/controls mixins) and extended to:
//   - fetch a presigned play link through the profile-new BFF (via $http);
//   - play HLS via hls.js (hls.js-FIRST — canPlayType is spoofable), with a
//     native <video>.src fallback for MSE-less engines (iPhone Safari) and mp4;
//   - transparently re-fetch the link on expiry / one media (or hls.js fatal)
//     error, restoring position and play state (spec §5.3 + §10 amendment).
// Resume (`_dmvideo_<contentId>`) is preserved byte-for-byte from default/.

// Inline fallbacks for the two configured locales (en / uk). vue-profile has NO
// local locale files — translations are fetched from the CMS at runtime — so a
// $t key that the CMS has not published yet would render as the raw key. These
// keep the error UI readable in both languages until (and unless) the CMS adds
// `videoUnavailable` / `videoTryAgain`.
const I18N_FALLBACK = {
    videoUnavailable: { en: "Video is unavailable", uk: "Відео недоступне" },
    videoTryAgain: { en: "Try again", uk: "Спробувати знову" },
};

export default {
    mixins: [chapters],
    name: "video-provider-r2",
    props: {
        data: {},
    },
    data() {
        return {
            time: "",
            currentItem: null,
            options: {
                debug: false,
                playsinline: true,
                settings: ["speed"],
                fullscreen: { enabled: true, iosNative: true },
            },

            selectedSpeed: 1,
            speedList: [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2],

            activeItem: null,
            isShowChapters: false,
            isShowSettings: false,

            // R2 playback state
            isLoading: true,
            hasError: false,
        };
    },

    computed: {
        errorText() {
            return this.tr("videoUnavailable");
        },
        retryText() {
            return this.tr("videoTryAgain");
        },
    },

    created() {
        this.$bus.$on("enter-chapter", ({ video, chapter }) => {
            this.$bus.$emit("event-select-video", video);
            this.changeChapter(chapter);
        });

        // Non-reactive playback machinery (deliberately off `data` — these are
        // imperative handles that must never trigger re-renders).
        // Watch URL → BFF; course `provider=r2` signed mp4 → play src directly.
        const src = this.data && this.data.src;
        const parsed = parseVideoSource(src);
        const providerIsR2 =
            this.data && String(this.data.provider || "").toLowerCase() === "r2";
        this._lessonId = parsed.kind === "r2" ? parsed.lessonId : null;
        this._directSrc =
            parsed.kind === "r2-direct"
                ? parsed.src
                : providerIsR2 && src && !this._lessonId
                  ? String(src)
                  : null;
        this._link = this._directSrc
            ? createDirectPlaybackLink(this._directSrc)
            : createPlaybackLink({
                  lessonId: this._lessonId,
                  http: $http,
                  // The BFF address is a stable convention (same spirit as the
                  // R2_WATCH_BASE literal) — baked in so builds work with zero CI
                  // variables; VUE_APP_VIDEO_PLAY_BFF overrides for local dev.
                  baseUrl: process.env.VUE_APP_VIDEO_PLAY_BFF || "https://edu-new.genius.space",
              });
        this._hls = null;
        this._destroyed = false;
        this._recovered = false; // one-silent-retry latch; re-armed by progress
        this._pendingRestore = null; // {time, wasPlaying} applied on next loadedmetadata
        this._media = null;
        this._listeners = [];
    },

    mounted() {
        const player = this.$refs.plyr && this.$refs.plyr.player;
        if (!player) return;
        const media = player.media;
        this._media = media;

        if (!this._lessonId && !this._directSrc) {
            // eslint-disable-next-line no-console
            console.error(
                "Unrecognized R2 playback URL:",
                this.data && this.data.src
            );
            this.hasError = true;
            this.isLoading = false;
            return;
        }

        const contentId = this.data && this.data.id;

        const on = (event, handler) => {
            media.addEventListener(event, handler);
            this._listeners.push([event, handler]);
        };

        // Resume mechanics — identical to default/: persist under the LMS
        // material id so the dispatcher's «continue» overlay works for R2 too.
        on("timeupdate", () => {
            if (this._destroyed) return;
            if (media.currentTime > 0) this._recovered = false; // progress re-arms retry
            if (contentId != null) {
                localStorage.setItem(`_dmvideo_${contentId}`, media.currentTime);
            }
        });

        // Proactive link refresh: a play/seek past expiresAt−10s must never hang
        // on a dead URL. Overlapping triggers coalesce (single-flight in the link
        // service).
        on("play", () => {
            if (this._destroyed) return;
            if (this._link.isExpired()) this.refreshLink();
        });
        on("seeking", () => {
            if (this._destroyed) return;
            if (this._link.isExpired()) this.refreshLink();
        });

        // Media error → ONE silent retry (re-fetch + restore); a second
        // consecutive error with no progress surfaces the error screen.
        on("error", () => {
            if (this._destroyed || !media.src) return;
            this.onFatal();
        });

        this.loadInitial();
    },

    beforeDestroy() {
        this._destroyed = true;
        this._link.invalidate(); // any in-flight refresh must not touch a dead element
        if (this._hls) {
            this._hls.destroy();
            this._hls = null;
        }
        if (this._media) {
            for (const [event, handler] of this._listeners) {
                this._media.removeEventListener(event, handler);
            }
        }
    },

    methods: {
        tr(key) {
            const value = this.$t(key);
            if (value && value !== key) return value;
            const lang =
                (this.$store &&
                    this.$store.getters.currentLanguage &&
                    this.$store.getters.currentLanguage.translationKey) ||
                "uk";
            const fallback = I18N_FALLBACK[key] || {};
            return fallback[lang] || fallback.uk || key;
        },

        async loadInitial() {
            this.isLoading = true;
            this.hasError = false;
            try {
                const payload = await this._link.load();
                if (this._destroyed) return;
                await this.applyLink(payload);
                if (this._destroyed) return;
                this.isLoading = false;
            } catch (e) {
                if (this._destroyed) return;
                this.showError(e);
            }
        },

        // Kind-aware source binding. hls.js (MSE) is tried FIRST for any 'hls'
        // link — canPlayType is spoofable (extensions fake native HLS then fail
        // to play a master.m3u8 as video.src). Native playback is only a
        // fallback for engines without MSE (iPhone Safari); plain mp4 assigns
        // video.src directly.
        async applyLink(payload) {
            const media = this._media;
            if (!media || !payload) return;

            // First-frame poster (contract: poster: <url>|null). Re-applied on
            // every refresh so a stale poster never survives a link change.
            if (payload.poster) media.poster = payload.poster;
            else media.removeAttribute("poster");

            if (payload.kind === "hls") {
                const epoch = this._link.epoch;
                const HlsMod = (await import("hls.js")).default;
                // The dynamic import is the only await here — re-check after it:
                // destroy() or a fatal error may have landed while the chunk
                // loaded (else: an orphaned hls instance / a resume behind the
                // error UI).
                if (this._destroyed || epoch !== this._link.epoch) return;
                if (HlsMod.isSupported()) {
                    if (!this._hls) {
                        this._hls = new HlsMod();
                        this._hls.on(HlsMod.Events.ERROR, (_evt, errData) => {
                            if (this._destroyed || !errData || !errData.fatal) return;
                            this.onFatal();
                        });
                        this._hls.attachMedia(media);
                    }
                    this._hls.loadSource(payload.src);
                    return;
                }
            }

            // mp4, or hls on a native-HLS engine without MSE.
            if (this._hls) {
                this._hls.destroy();
                this._hls = null;
            }
            media.src = payload.src;
        },

        async refreshLink() {
            const media = this._media;
            if (!media) return;
            // Snapshot restore target BEFORE the async fetch swaps the source.
            const restore = { time: media.currentTime || 0, wasPlaying: !media.paused };
            try {
                const payload = await this._link.refresh();
                if (this._destroyed || !payload) return; // null == invalidated
                this._pendingRestore = restore;
                await this.applyLink(payload);
                if (this._destroyed) return;
                this.applyPendingRestore();
            } catch (e) {
                if (this._destroyed) return;
                this.showError(e);
            }
        },

        applyPendingRestore() {
            const media = this._media;
            const restore = this._pendingRestore;
            if (!media || !restore) return;

            const apply = () => {
                media.removeEventListener("loadedmetadata", apply);
                if (this._destroyed) return;
                try {
                    media.currentTime = restore.time;
                } catch (e) {
                    // seeking before metadata is ready — ignore, timeupdate self-heals
                }
                if (restore.wasPlaying) {
                    const p = media.play();
                    if (p && p.catch) p.catch(() => {});
                }
                this._pendingRestore = null;
            };

            if (media.readyState >= 1) apply();
            else media.addEventListener("loadedmetadata", apply);
        },

        // Media <video> error OR hls.js fatal error: one transparent retry, then
        // give up. A second consecutive failure with no progress in between
        // (recovered still true) invalidates any in-flight refresh and surfaces
        // the error screen — nothing may resume behind it.
        onFatal() {
            if (this._recovered) {
                this._link.invalidate();
                this.showError();
                return;
            }
            this._recovered = true;
            this.refreshLink();
        },

        showError(e) {
            if (e && e.message) {
                // eslint-disable-next-line no-console
                console.error("[r2-provider] play link error:", e.message);
            }
            this.isLoading = false;
            this.hasError = true;
        },

        retry() {
            this._recovered = false;
            this.loadInitial();
        },

        onTouchPause() {
            const elVideo = document.getElementById("video");
            if (elVideo && elVideo.classList.contains("has-play")) {
                this.$refs.plyr.player.pause();
            }
        },
    },
};
